import {
  getElecFutureProductsChart,
  getElecFutureProductsDailyIndicators,
} from "$/api/market-monitoring/electricity.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { GraphIndicators } from "$/lib/components/graphs/GraphIndicator/GraphIndicators";
import LineGraph from "$/lib/components/graphs/LineGraph";
import { Tabs } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import { DEFAULT_GRAPH_TABS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  BASE_ELEC_COLORS,
  DEFAULT_GRAPH_COLORS,
  handleOnChangeDefaultGraphsTabs,
  PEAK_ELEC_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

type Props = {
  title: string;
  period: FutureProductPeriod;
  selectedDate: Date;
} & CustomDashboardProps;

const FutureProductsChart = ({
  id,
  title,
  period,
  selectedDate: defaultSelectedDate,
  isShowcase = false,
  isDraggable = false,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date>(defaultSelectedDate);

  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<{
    base: boolean;
    peak: boolean;
  }>({
    base: true,
    peak: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-future-prod-chart", dateRange, period],
    queryFn: () =>
      getElecFutureProductsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-elec-future-prod-chart-daily", selectedDate, period],
    queryFn: () =>
      getElecFutureProductsDailyIndicators({
        date: getDate(selectedDate),
        period,
      }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.base && filters.peak
          ? data.mergedData
          : filters.base
            ? data.baseLoadProductsData
            : filters.peak
              ? data.peakLoadProductsData
              : []
        : [],
    [data, filters],
  );
  const toDisplayVolatility = useMemo(
    () =>
      data
        ? filters.base && filters.peak
          ? data.mergedVolatilityData
          : filters.base
            ? data.baseLoadProductsVolatilityData
            : filters.peak
              ? data.peakLoadProductsVolatilityData
              : []
        : [],
    [data, filters],
  );

  const baseKeys = useMemo(
    () => getArrayKeys(data?.baseLoadProductsData ?? []),
    [data],
  );
  const peakKeys = useMemo(
    () => getArrayKeys(data?.peakLoadProductsData ?? []),
    [data],
  );

  const keys = useMemo(
    () =>
      filters.base && filters.peak
        ? [...baseKeys, ...peakKeys]
        : filters.base
          ? baseKeys
          : filters.peak
            ? peakKeys
            : [],
    [baseKeys, filters, peakKeys],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const baseColors = useMemo(
    () =>
      [...BASE_ELEC_COLORS, ...DEFAULT_GRAPH_COLORS].slice(0, baseKeys.length),
    [baseKeys],
  );
  const peakColors = useMemo(
    () =>
      [...PEAK_ELEC_COLORS, ...DEFAULT_GRAPH_COLORS].slice(0, peakKeys.length),
    [peakKeys.length],
  );

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]:
            filters.base && filters.peak
              ? [...baseColors, ...peakColors][i]
              : filters.base
                ? baseColors[i]
                : filters.peak
                  ? peakColors[i]
                  : DEFAULT_GRAPH_COLORS[i],
        }),
        {},
      ),
    [baseColors, filters, keys, peakColors],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color:
          filters.base && filters.peak
            ? [...baseColors, ...peakColors][i]
            : filters.base
              ? baseColors[i]
              : filters.peak
                ? peakColors[i]
                : DEFAULT_GRAPH_COLORS[i],
      })),
    );
  }, [keys, filters, baseColors, peakColors]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title={title}
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      // isLoading={isLoading}
      MiddleComponent={
        <Tabs
          className="mx-auto"
          type="bordered"
          tabs={DEFAULT_GRAPH_TABS}
          activeTab={selectedGraphTabId}
          handleTabChange={setSelectedGraphTabId}
        />
      }
      id={id}
      isDraggable={isDraggable}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox
            fullWidth
            fullHeight
            justify="between"
            className="gap-2 rounded-3xl"
          >
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId={`id-elec-${period}`}
                colors={colors}
                theme="blue"
                keys={keys}
                heightPercent={55}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId={`id-elec-${period}`}
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="blue"
                keys={keys}
                heightPercent={28}
                yAxisTicksNumber={7}
                leftMargin={-10}
                indices={indices}
                setIndices={setIndices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
          />

          <CheckboxSet<"base" | "peak">
            id={`base-peak-${period}`}
            title="Base / Peak"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.base && !newValue.peak) return prev;
                return newValue;
              })
            }
            options={[
              { id: "base", label: "Base" },
              { id: "peak", label: "Peak" },
            ]}
          />

          <Flexbox className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.length && (
              <Flexbox fullWidth>
                <GraphIndicators
                  selectedDate={selectedDate}
                  data={daily ?? []}
                  indicatorsTitles={{
                    title1: "Base",
                    title2: "Peak",
                  }}
                />
              </Flexbox>
            )}
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default FutureProductsChart;
